// client/src/components/forms/SelectMenu.tsx — the application's own list for every native
// `<select>` · the console's visual pass, second round (D-1839).
//
// ── What it is ────────────────────────────────────────────────────────────────────────────
// A `<select>`'s closed box is ours to style and has been since UI-002; the list that drops
// out of it is browser chrome — system font, system colours, no radius, no hover, and on
// Windows a different list in every browser. D-1676 replaced the date control's popup for the
// same reason. This component replaces the select's, for all 131 selects in the console at
// once, without touching one of them.
//
// ── How, and why not a `<Select>` component ───────────────────────────────────────────────
// Mounted ONCE, at the composition root (`main.tsx`). It was once per shell, which left the
// tenant portal, the sign-in screens and the link flows with the browser's list (D-3330).
// It listens for a pointer landing on any `<select>` under `document`, cancels the browser's
// own list, and opens a `role="listbox"` of the same options under the
// same box. Choosing writes `select.value` and dispatches a bubbling `change` — the event React
// listens for on a select, so `onChange`, React Hook Form's `register` and every search-param
// filter see a change they cannot tell from a native one.
//
// The select itself stays the control: it keeps focus, its label, its `aria-invalid`, its
// place in the tab order and its `getByRole('combobox')` identity. That is what lets this be
// one file rather than a migration of 54: a wrapping component would have had to become the
// combobox, and every one of the suite's `selectOptions` calls names the native one.
//
// ── Who gets the native list, on purpose ──────────────────────────────────────────────────
// • A touch pointer. The phone's picker is a wheel a thumb can drive; a floating list at 360 px
//   is not better, and §11.2's field flows are phone flows.
// • The keyboard, mostly. A focused select changes value on ArrowUp/Down without opening
//   anything, and screen readers drive it that way; nothing here intercepts that. Only the two
//   keys whose native meaning IS „open the list" — Space, and Alt+ArrowDown — open ours instead,
//   so a keyboard user who wants a list gets the same one a mouse user gets.
// • An untrusted event. `isTrusted` is false for everything a test dispatches, so under jsdom
//   this component is inert and `userEvent.selectOptions` drives the native select as it always
//   did. Tests of THIS component open it through the `SELECT_MENU_OPEN_EVENT` seam below.
// • `multiple` and `size > 1` selects, which are lists already.
//
// ── Where it renders ──────────────────────────────────────────────────────────────────────
// Into the nearest open `<dialog>` when the select is inside one, else `document.body`. A
// native dialog paints in the top layer, above every z-index on the page, so a list portaled to
// the body would open UNDER the modal it was asked from. `.modal` is `position: fixed; inset: 0`
// with no transform, so `position: fixed` coordinates inside it are viewport coordinates.
//
// The list also states its own alignment. It is portaled into the `<dialog>` ELEMENT, not
// into the `.modal-box` that D-1943 set `text-left` on, and a dialog inherits `text-align`
// from wherever it was mounted — the rent board's right-aligned action cell, for one, from
// which every option came out flush right with its check mark on the wrong side. A thing that
// paints outside its own layout says which way its text runs (D-2101).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { fold } from '@/lib/text';

/** Dispatched on `document` with a `<select>` as `detail` to open the list programmatically. */
export const SELECT_MENU_OPEN_EVENT = 'prop360:select-menu-open';

/**
 * The attribute the list carries, so a press inside it can be recognised from the outside.
 *
 * The list is PORTALED — to the nearest open `<dialog>` or to `document.body` — which means it
 * is not a DOM descendant of whatever the select sits in. Any panel that closes itself on a
 * press outside its own subtree therefore reads a press on an option as a press outside itself,
 * and shuts while the user is choosing (D-2110). Such a panel calls `isInsideSelectMenu` below
 * before it believes an outside press.
 */
export const SELECT_MENU_ATTRIBUTE = 'data-select-menu';

/** Whether a press landed inside the application's own select list, wherever it was portaled. */
export function isInsideSelectMenu(node: Node | null): boolean {
  const element = node instanceof Element ? node : (node?.parentElement ?? null);

  return element !== null && element.closest(`[${SELECT_MENU_ATTRIBUTE}]`) !== null;
}

interface MenuOption {
  readonly index: number;
  readonly value: string;
  readonly label: string;
  readonly disabled: boolean;
  /** The `<optgroup label>` this option sits under, when it does; rendered as a caption. */
  readonly group: string | null;
}

interface OpenMenu {
  readonly select: HTMLSelectElement;
  readonly options: readonly MenuOption[];
  readonly host: HTMLElement;
}

interface Placement {
  readonly top: number;
  readonly left: number;
  readonly minWidth: number;
  readonly maxHeight: number;
  readonly above: boolean;
}

const MENU_GAP = 4;
const MENU_MAX_HEIGHT = 288;
const VIEWPORT_MARGIN = 8;

function readOptions(select: HTMLSelectElement): MenuOption[] {
  return Array.from(select.options).flatMap((option, index) => {
    if (option.hidden) return [];
    const group =
      option.parentElement instanceof HTMLOptGroupElement ? option.parentElement.label : null;
    return [
      {
        index,
        value: option.value,
        label: option.label || option.text,
        disabled: option.disabled,
        group,
      },
    ];
  });
}

/** The select's visible name, for the listbox: its `<label>`, else its `aria-label`. */
function accessibleName(select: HTMLSelectElement, fallback: string): string {
  const label = select.labels?.[0]?.textContent?.trim();
  return label || select.getAttribute('aria-label') || fallback;
}

function placeUnder(select: HTMLSelectElement): Placement {
  const rect = select.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
  const aboveSpace = rect.top - MENU_GAP - VIEWPORT_MARGIN;
  const above = below < Math.min(MENU_MAX_HEIGHT, 160) && aboveSpace > below;
  const maxHeight = Math.max(96, Math.min(MENU_MAX_HEIGHT, above ? aboveSpace : below));
  return {
    top: above ? rect.top - MENU_GAP : rect.bottom + MENU_GAP,
    left: Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.left, window.innerWidth - rect.width - VIEWPORT_MARGIN),
    ),
    minWidth: rect.width,
    maxHeight,
    above,
  };
}

function firstEnabled(options: readonly MenuOption[], from: number, step: 1 | -1): number {
  let i = from;
  while (i >= 0 && i < options.length) {
    if (!options[i]?.disabled) return i;
    i += step;
  }
  return -1;
}

export function SelectMenu() {
  const { t } = useTranslation();
  const [menu, setMenu] = useState<OpenMenu | null>(null);
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Typeahead: the letters typed in the last second, matched against the option labels.
  const typed = useRef<{ text: string; at: number }>({ text: '', at: 0 });
  // `pointerType` of the last pointer to go down, read by the `mousedown` handler that decides.
  const lastPointerType = useRef<string>('mouse');

  const close = useCallback(() => {
    setMenu((current) => {
      current?.select.removeAttribute('aria-expanded');
      return null;
    });
  }, []);

  const open = useCallback((select: HTMLSelectElement) => {
    const options = readOptions(select);
    if (options.length === 0) return;
    const host = select.closest('dialog') ?? document.body;
    const selected = options.findIndex((option) => option.index === select.selectedIndex);
    setActive(selected >= 0 ? selected : Math.max(0, firstEnabled(options, 0, 1)));
    setPlacement(placeUnder(select));
    select.setAttribute('aria-expanded', 'true');
    setMenu({ select, options, host });
  }, []);

  const commit = useCallback(
    (option: MenuOption) => {
      if (!menu || option.disabled) return;
      const { select } = menu;
      if (select.selectedIndex !== option.index) {
        select.selectedIndex = option.index;
        // `input` then `change`, bubbling — the pair a native pick produces, in that order.
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      close();
      select.focus();
    },
    [menu, close],
  );

  // The document-level seam: a pointer on a select, the two keys that mean „open", and the
  // programmatic event tests use.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      lastPointerType.current = event.pointerType;
    };
    const onMouseDown = (event: MouseEvent) => {
      if (!event.isTrusted || event.button !== 0) return;
      if (lastPointerType.current === 'touch') return;
      const target = event.target instanceof Element ? event.target.closest('select') : null;
      if (!target || target.disabled || target.multiple || target.size > 1) return;
      event.preventDefault();
      if (menu?.select === target) {
        close();
        return;
      }
      target.focus();
      open(target);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.isTrusted) return;
      const target = event.target instanceof HTMLSelectElement ? event.target : null;
      if (!target || target.disabled || target.multiple || target.size > 1) return;
      const opensList = event.key === ' ' || (event.altKey && event.key === 'ArrowDown');
      if (opensList && !menu) {
        event.preventDefault();
        open(target);
      }
    };
    const onProgrammaticOpen = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (detail instanceof HTMLSelectElement) open(detail);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener(SELECT_MENU_OPEN_EVENT, onProgrammaticOpen);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener(SELECT_MENU_OPEN_EVENT, onProgrammaticOpen);
    };
  }, [menu, open, close]);

  // While open: the keyboard drives the list, focus leaving the select closes it, a click
  // elsewhere closes it, and scrolling or resizing moves it with its select.
  useEffect(() => {
    if (!menu) return;
    const { select, options } = menu;

    const onKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          setActive((i) => {
            const next = firstEnabled(options, i + 1, 1);
            return next === -1 ? i : next;
          });
          return;
        }
        case 'ArrowUp': {
          event.preventDefault();
          setActive((i) => {
            const prev = firstEnabled(options, i - 1, -1);
            return prev === -1 ? i : prev;
          });
          return;
        }
        case 'Home':
          event.preventDefault();
          setActive(Math.max(0, firstEnabled(options, 0, 1)));
          return;
        case 'End':
          event.preventDefault();
          setActive(Math.max(0, firstEnabled(options, options.length - 1, -1)));
          return;
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const option = options[active];
          if (option) commit(option);
          return;
        }
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          close();
          return;
        case 'Tab':
          close();
          return;
        default: {
          if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
          event.preventDefault();
          const now = Date.now();
          const text =
            (now - typed.current.at < 1000 ? typed.current.text : '') + fold(event.key);
          typed.current = { text, at: now };
          const start = text.length === 1 ? active + 1 : active;
          const order = [...options.slice(start), ...options.slice(0, start)];
          // Folded on both sides (D-2246): typing „t" has to reach „Țeavă spartă", and a
          // keyboard without the Romanian layout is the ordinary case, not the exception.
          const hit = order.find((o) => !o.disabled && fold(o.label).startsWith(text));
          if (hit) setActive(options.indexOf(hit));
        }
      }
    };
    const onFocusOut = () => {
      // Deferred one tick: a click on an option moves focus nowhere (its mousedown is
      // cancelled), but a Tab has already moved it by the time this runs.
      window.setTimeout(() => {
        if (document.activeElement !== select) close();
      }, 0);
    };
    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target instanceof Node ? event.target : null;
      if (target && (listRef.current?.contains(target) || select.contains(target))) return;
      close();
    };
    let frame = 0;
    const reposition = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setPlacement(placeUnder(select));
      });
    };

    select.addEventListener('keydown', onKeyDown);
    select.addEventListener('focusout', onFocusOut);
    document.addEventListener('mousedown', onDocumentMouseDown);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      select.removeEventListener('keydown', onKeyDown);
      select.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('mousedown', onDocumentMouseDown);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      cancelAnimationFrame(frame);
    };
  }, [menu, active, commit, close]);

  // Keep the active row in view as the keyboard moves it.
  useLayoutEffect(() => {
    if (!menu) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [menu, active]);

  if (!menu || !placement) return null;

  const listboxId = `select-menu-${menu.select.id || 'list'}`;
  let lastGroup: string | null = null;

  return createPortal(
    <div
      ref={listRef}
      id={listboxId}
      role="listbox"
      {...{ [SELECT_MENU_ATTRIBUTE]: '' }}
      aria-label={accessibleName(menu.select, t('common.selectMenu.options'))}
      className="pop-enter fixed z-50 overflow-y-auto rounded-xl border border-base-300 bg-base-100 py-1 text-left shadow-lg"
      style={{
        top: placement.above ? undefined : placement.top,
        bottom: placement.above ? window.innerHeight - placement.top : undefined,
        left: placement.left,
        minWidth: placement.minWidth,
        maxHeight: placement.maxHeight,
        transformOrigin: placement.above ? 'bottom' : 'top',
      }}
    >
      {menu.options.map((option, i) => {
        const caption = option.group !== null && option.group !== lastGroup ? option.group : null;
        lastGroup = option.group;
        const selected = option.index === menu.select.selectedIndex;
        const isActive = i === active;
        return (
          <div key={`${option.index}-${option.value}`}>
            {caption !== null ? (
              <div className="px-3 pt-2 pb-1 text-[11px] font-semibold tracking-[0.08em] text-gray-500 uppercase">
                {caption}
              </div>
            ) : null}
            <div
              role="option"
              data-index={i}
              aria-selected={selected}
              aria-disabled={option.disabled || undefined}
              className={`mx-1 flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm transition-colors duration-150 ${
                option.disabled
                  ? 'cursor-not-allowed text-gray-400'
                  : isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-base-content hover:bg-base-200'
              } ${selected && !option.disabled ? 'font-semibold' : ''}`}
              // Cancelled so the select keeps focus and its `focusout` never fires for a click
              // on the list; `click` then commits.
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onMouseEnter={() => {
                if (!option.disabled) setActive(i);
              }}
              onClick={() => {
                commit(option);
              }}
            >
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {selected ? <Icon name="check" className="h-4 w-4 shrink-0 text-primary" /> : null}
            </div>
          </div>
        );
      })}
    </div>,
    menu.host,
  );
}
